import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import intlTelInput, { SomeOptions } from 'intl-tel-input';
import { de as intTelDE,fr as intTelFR,es as intTelES,en as intTelEN } from "intl-tel-input/i18n"; 
import { I18n } from 'intl-tel-input/i18n/types';


@Component({
    selector: 'lib-phone-number-input',
    templateUrl: './phone-number-input.component.html',
    styleUrls: ['./phone-number-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneNumberInputComponent),
            multi: true
        }, { provide: APP_BASE_HREF, useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(), deps: [PlatformLocation] }
    ],
    standalone: false
})
export class PhoneNumberInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {

  @ViewChild('phoneNumberField', { static: false }) inputRef: ElementRef;
  @Input() onlyCountries: string[];
  @Input() initialCountry: string;
  @Input() localizedCountries: any;
  @Input() dropdownContainer: HTMLElement;
  @Input() focus: boolean;

  isDisabled = false;
  intElement: any;
  isTouched = false;


  initValue = null;

  private onTouched!: Function;
  private onChanged!: Function;

  constructor(@Inject(APP_BASE_HREF) public baseHref: string, @Inject('language') private language: string) {
    const stylesCSS = document.createElement('link');
    stylesCSS.id = 'intlTelInput.min.css';
    stylesCSS.rel = 'stylesheet';
    stylesCSS.href = `${this.baseHref}assets/intl-tel-input/css/intlTelInput.min.css`;
    const head = document.getElementsByTagName('head')[0];
    head.appendChild(stylesCSS);
  }
  ngOnDestroy(): void {
    if (this.intElement) {
      this.intElement.destroy();
    }
  }

  ngAfterViewInit(): void {
    let intTelLanguage : I18n = intTelEN;

    switch (this.language) {
      case 'de':
        intTelLanguage = intTelDE;
        break;
      case 'fr':
        intTelLanguage = intTelFR;
        break;
      case 'es':
        intTelLanguage = intTelES;
        break;
      default:
        intTelLanguage = intTelEN;
    }
    
    setTimeout(()=>{
      const initParams = {
        initialCountry: "de",
        i18n:intTelLanguage,
        dropdownContainer:this.dropdownContainer,
        autoPlaceholder: 'off',
        validationNumberTypes: ['FIXED_LINE_OR_MOBILE'],
        nationalMode: true,
        loadUtils: () => {
          return import('intl-tel-input/build/js/utils.js');
        }
      } as unknown as SomeOptions;
      if (this.onlyCountries) {
        initParams['onlyCountries'] = this.onlyCountries;
      }
      
      if (this.initialCountry) {
        initParams['initialCountry'] = this.initialCountry;
      }
  
      if (this.localizedCountries) {
        initParams['localizedCountries'] = this.localizedCountries;
      }
  
      this.intElement = intlTelInput(this.inputRef.nativeElement, initParams);
     
      if (this.initValue) {
        this.applyData(this.initValue);
      }
      this.inputRef.nativeElement.addEventListener("keyup", () => {
        this.isTouched = true;
        this.onTouched();
        this.emitUpdate();
      });
      this.inputRef.nativeElement.addEventListener("countrychange", () =>  {
        this.isTouched = true;
        this.onTouched();
        this.emitUpdate();
      });

      if (this.focus){
        this.inputRef.nativeElement.focus();
      }
    },200);
  }

  writeValue(obj: any): void {
    if (this.intElement) {
      this.applyData(obj);
    } else {
      this.initValue = obj;
    }
  }

  applyData(number: any): void {
    this.intElement.setNumber(number);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  emitUpdate(): void {
    if (this.intElement.isValidNumber() || this.intElement.getNumber() === '') {
      this.onChanged(this.intElement.getNumber());
    }
  }
 
}
